import type { ApolloClient } from '@apollo/client';

import { currentLoggedUserVar } from '@/shared/api';
import { UPDATE_DATA_GRID_PREFERENCE, type UpdateDataGridPreferenceResponse } from '@/shared/api/graphql/administration';
import { toKebabCase } from '@/shared/lib/util/string';
import type { GridPrefs } from '@/shared/model/DataGridPreferencesStore';
import { GRID_PREFS_VERSION, useDataGridPreferencesStore } from '@/shared/model/DataGridPreferencesStore';

export const MIGRATION_FLAG_KEY = 'dataGridPrefsMigratedAt';
export const LEGACY_SORT_KEY = 'data-grid-sort-storage';
export const LEGACY_GRID_STATE_SUFFIX = '_grid-state';

type LegacyGridStateEntry = {
  columns?: GridPrefs['columns'];
  pinnedColumns?: GridPrefs['pinnedColumns'];
};

type LocalStorageOutput = Record<string, { entry: LegacyGridStateEntry; sourceKey: string }>;

function parseJsonOrNull<T = unknown>(raw: string | null): T | null {
  if (!raw) return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

function collectFromLocalStorage(): LocalStorageOutput {
  const out: LocalStorageOutput = {};

  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (!key || !key.endsWith(LEGACY_GRID_STATE_SUFFIX)) continue;

    const parsed = parseJsonOrNull<LegacyGridStateEntry>(localStorage.getItem(key));
    if (!parsed) continue;

    const gridName = toKebabCase(key.slice(0, -LEGACY_GRID_STATE_SUFFIX.length));
    out[gridName] = {
      entry: { columns: parsed.columns, pinnedColumns: parsed.pinnedColumns },
      sourceKey: key
    };
  }

  return out;
}

function toGridPrefs(entry: LegacyGridStateEntry): GridPrefs {
  return {
    v: GRID_PREFS_VERSION,
    updatedAt: new Date().toISOString(),
    columns: entry.columns,
    pinnedColumns: entry.pinnedColumns
  };
}

/**
 * One-shot migration of legacy localStorage-backed grid preferences to the
 * server-side `dataGridPreferences` blob. Safe to call multiple times: an
 * idempotency flag skips the work after a complete successful run.
 *
 * Sort is intentionally NOT migrated — the legacy `data-grid-sort-storage`
 * key is simply deleted.
 *
 * Must be called *after* {@link useDataGridPreferencesStore.hydrateFromPreferences}.
 */
export async function migrateDataGridPreferences(client: ApolloClient<unknown>): Promise<void> {
  if (typeof window === 'undefined') return;

  if (localStorage.getItem(MIGRATION_FLAG_KEY)) return;

  // Drop the legacy sort key unconditionally — sort is no longer persisted.
  localStorage.removeItem(LEGACY_SORT_KEY);

  const collected = collectFromLocalStorage();
  const gridNames = Object.keys(collected);

  if (gridNames.length === 0) {
    localStorage.setItem(MIGRATION_FLAG_KEY, new Date().toISOString());
    return;
  }

  let failed = false;

  const { setPrefs } = useDataGridPreferencesStore.getState();

  for (const gridName of gridNames) {
    const { entry, sourceKey } = collected[gridName];

    // Read the latest snapshot inside the loop — a previous iteration may have
    // just written to the store, and two legacy keys can collide after kebab-casing.
    if (useDataGridPreferencesStore.getState().prefs[gridName]) {
      // Server already has prefs for this grid — server wins; drop the local key.
      localStorage.removeItem(sourceKey);
      continue;
    }

    const newPrefs = toGridPrefs(entry);

    try {
      const result = await client.mutate({
        mutation: UPDATE_DATA_GRID_PREFERENCE,
        variables: { gridName, preferences: JSON.stringify(newPrefs) }
      });

      const mutationData = (result.data as UpdateDataGridPreferenceResponse | null | undefined)?.updateDataGridPreference;
      const status = mutationData?.status;

      if (status?.toLowerCase() === 'success') {
        setPrefs(gridName, newPrefs);
        localStorage.removeItem(sourceKey);

        // Keep the persisted reactive var in sync with the server so the next
        // boot-time hydration sees the migrated prefs (not the stale snapshot
        // from the last login response).
        const updated = mutationData?.preferences;
        if (updated) {
          const currentUser = currentLoggedUserVar();
          if (currentUser) currentLoggedUserVar({ ...currentUser, preferences: updated });
        }
      } else {
        failed = true;
      }
    } catch (e) {
      console.warn(`Failed to migrate grid prefs for ${gridName}`, e);
      failed = true;
    }
  }

  if (!failed) {
    localStorage.setItem(MIGRATION_FLAG_KEY, new Date().toISOString());
  }
}
